/**
 * The window's API client. Same origin as the admin's (the Worker forwards
 * /api/* over a service binding), same cookies: `__Host-tix_device` from
 * pairing and `__Host-tix_staff` from the PIN login.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

async function toError(response: Response): Promise<ApiError> {
  let body: { error?: { code?: string; message?: string; details?: Record<string, unknown> } } = {};
  try {
    body = await response.json();
  } catch {
    // non-JSON
  }
  return new ApiError(response.status, body.error?.code ?? 'UNKNOWN', body.error?.message ?? `Request failed (${response.status})`, body.error?.details);
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: { accept: 'application/json', ...(init.body ? { 'content-type': 'application/json' } : {}), ...(init.method && init.method !== 'GET' ? { 'x-bre-csrf': '1' } : {}), ...(init.headers as Record<string, string> | undefined) },
  });
  if (!response.ok) throw await toError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) }),
  patch: <T>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  del: <T>(path: string, body?: unknown) => request<T>(path, { method: 'DELETE', ...(body ? { body: JSON.stringify(body) } : {}) }),
};

// --- Shapes ------------------------------------------------------------------------

export interface SessionResponse {
  staff: { id: string; display_name: string; status: string };
  session: { id: string; surface: string; auth_method: string; device_id: string | null; expires_at: string; reauthenticated_at: string };
  venue_id: string | null;
  permissions: string[];
}

export interface Register {
  id: string;
  venue_id: string;
  code: string;
  name: string;
  default_payment_terminal_id: string | null;
  default_ticket_printer_id: string | null;
  default_receipt_printer_id: string | null;
  allow_manual_card: number;
  blind_close: number;
}

export interface RegisterSession {
  id: string;
  register_id: string;
  status: string;
  payment_terminal_id: string | null;
  ticket_printer_id: string | null;
  receipt_printer_id: string | null;
  opened_at: string;
  business_date: string;
}

export interface Shift {
  id: string;
  status: string;
  cashier_staff_account_id: string;
  opened_at: string;
  opening_float_cents: number;
  closed_at: string | null;
  expected_close_cents: number | null;
  counted_close_cents: number | null;
  over_short_cents: number | null;
}

export interface WindowContext {
  staff: { id: string; display_name: string };
  device: { id: string; name: string; venue_id: string | null; register_id: string | null };
  venue: { id: string; name: string; timezone: string } | null;
  register: Register | null;
  session: RegisterSession | null;
  shift: Shift | null;
  open_shifts: { id: string; cashier_name: string; opened_at: string; opening_float_cents: number }[];
  terminals: { id: string; label: string; register_id: string | null; simulated: number; last_known_status: string | null }[];
  printers: { id: string; name: string; register_id: string | null; printer_model: string; last_status: string | null }[];
  drawers: { id: string; name: string; code: string; register_id: string | null }[];
  registers: Register[];
  business_date: string;
}

export interface DrawerExpected {
  opening_float_cents: number;
  cash_sales_cents: number;
  cash_refunds_cents: number;
  paid_in_cents: number;
  paid_out_cents: number;
  expected_cents: number;
  cash_sale_count: number;
  cash_refund_count: number;
}

export interface WindowPerformance {
  id: string;
  event_id: string;
  event_title: string;
  status: string;
  starts_at: string;
  doors_at: string | null;
  timezone: string;
  venue_name: string;
  sellable: boolean;
  inventory_initialized_at: string | null;
}

export interface PerformanceDetail {
  performance: WindowPerformance & { manifest_id: string; ticket_limit_per_order: number | null };
  zones: { id: string; code: string; name: string; color_hex: string | null }[];
  ticket_types: { id: string; code: string; name: string; category: string; is_comp_type: number; timed_entry: number }[];
  prices: Record<string, Record<string, { face_cents: number; total_cents: number; fees_cents: number } | null>>;
  comp_reasons: { id: string; code: string; name: string }[];
  layout_url: string;
  background_url: string;
}

export interface CartSeat {
  unit_id: string;
  seat_id: string | null;
  ga_area_id: string | null;
  area: string | null;
  section: string | null;
  table: string | null;
  row: string | null;
  seat_number: string | null;
  ticket_type_id: string;
  ticket_type_name: string | null;
  discount_cents: number;
  comp_reason_id: string | null;
  comp_reason_name: string | null;
  package_key?: string | null;
  pass_id?: string | null;
  /** The entry time this was sold against, on a timed show (D-232). */
  timed_entry_slot_id?: string | null;
  entry_time?: string | null;
  total_cents: number | null;
  face_cents: number | null;
  expires_at: string;
}

/** What is known about a buyer, shown at the counter (D-270). */
export interface CustomerFlag {
  id: string;
  flag_type: string;
  name: string;
  reason: string;
  warns_at_sale: boolean;
  created_at: string;
}

/**
 * What staff have written about the attached customer (D-358): a count and the
 * latest note, their tags, and a warning for any tag that should stop a sale
 * long enough to read it, with the note that explains it.
 */
export interface CustomerRecord {
  notes_count: number;
  latest_note: { body: string; author_name: string | null; created_at: string } | null;
  tags: { code: string; name: string; warns_at_sale: boolean }[];
  warnings: { tag_code: string; name: string; note: { body: string; author_name: string | null; created_at: string } | null }[];
}

export interface CartView {
  cart_id: string;
  expires_at: string | null;
  customer: { sub: string | null; name: string | null; email: string | null; phone: string | null; organization_name?: string | null; organization_id?: string | null } | null;
  performances: { performance_id: string; title: string; starts_at: string; timezone: string; venue_name: string; seats: CartSeat[]; subtotal: { customer_total_cents: number } | null; pricing_problem: string | null }[];
  packages: { key: string; product_id: string; code: string; name: string; kind: string; price_cents: number; quantity: number; unit_ids: string[]; total_cents: number }[];
  totals: { face_value_cents: number; discount_cents: number; comp_cents: number; fees_cents: number; tax_cents: number; customer_total_cents: number; tickets: number };
  /** A promo code on the sale (D-359), and why one was just taken off if it was. */
  promotion?: { code: string; name: string; discount_cents: number; tickets: number } | null;
  promotion_removed_reason?: string | null;
  /** The automatic deals on the sale's games (D-377), each its own line; never alongside a code. */
  automatic_promotions?: { promotion_id: string; name: string; discount_cents: number; tickets: number }[];
  /** A code the customer gave that is kept but not applied, because a deal saves them more. */
  promo_code_entered?: string | null;
  /** Which was applied, and the sentence to read out. */
  promotion_choice?: { applied: 'CODE' | 'AUTOMATIC'; message: string } | null;
  /** Extras on the sale (D-377: a promotion's concession special), and what is on offer beside its games. */
  add_ons?: { add_on_product_id: string; name: string; quantity: number; unit_price_cents: number; amount_cents: number }[];
  add_ons_offered?: { id: string; name: string; description: string | null; price_cents: number; max_per_order: number; remaining: number | null }[];
  /** Comp vouchers redeemed into this sale (D-373); used when it completes. */
  vouchers?: { voucher_id: string; code: string; performance_id: string; unit_ids: string[] }[];
}

/** One map of a package's seats: pick here, and the covered performances take the same seats (D-68, D-330). */
export interface PackageMapStep {
  step: number;
  performance_id: string;
  event_slug: string | null;
  event_title: string | null;
  venue_name: string | null;
  starts_at: string | null;
  ticket_type: string | null;
  seats_per_package: number;
  covers: { performance_id: string; event_title: string | null; starts_at: string | null }[];
}

export interface WindowPackage {
  id: string;
  code: string;
  name: string;
  description: string | null;
  package_kind: string;
  price_cents: number | null;
  face_total_cents: number | null;
  min_selections: number | null;
  max_selections: number | null;
  max_per_order: number;
  /** The seating areas it is sold in (D-384); empty is anywhere. */
  areas?: { code: string; name: string }[];
  components: { id: string; event_title: string | null; performance_id: string | null; performance_starts_at: string | null; timezone: string | null; ticket_type_name: string | null; quantity: number; required: number; sellable: boolean }[];
}

export type Tender =
  | { tender_type: 'CASH'; amount_cents: number; cash_tendered_cents?: number | null }
  | { tender_type: 'CHECK'; amount_cents: number; check_number: string }
  // Settled in the previous system; nothing is collected now (D-251).
  | { tender_type: 'PRIOR_SYSTEM'; amount_cents: number; reference: string }
  | { tender_type: 'HOUSE_ACCOUNT'; amount_cents: number; organization_id: string }
  | { tender_type: 'CARD_TERMINAL' | 'CARD_KEYED'; amount_cents: number; payment_intent_id: string }
  /** D-75: a gift card and a loyalty reward are window tenders too, where this deployment has them (D-369). */
  | { tender_type: 'GIFT_CARD'; amount_cents: number; gift_card_code: string }
  | { tender_type: 'LOYALTY_REWARD'; amount_cents: number; reward_id: string }
  /** The attached customer's account credit (D-372): a sale tender only. */
  | { tender_type: 'ACCOUNT_CREDIT'; amount_cents: number };

export interface SaleResult {
  order_id: string;
  order_number: string;
  total_cents: number;
  change_cents: number;
  ticket_ids: string[];
  print_job_ids: string[];
  receipt_job_id: string | null;
  emailed: boolean;
}

export interface ExchangeQuote {
  order_id: string;
  order_number: string;
  event_id: string;
  event_title: string | null;
  out: { ticket_id: string; order_item_id: string; seat_label: string; ticket_type: string | null; performance_starts_at: string | null; value_cents: number }[];
  in: { unit_id: string; performance_id: string; seat_label: string; ticket_type: string | null; performance_starts_at: string; value_cents: number }[];
  original_value_cents: number;
  new_value_cents: number;
  /** The exchange policy's fee included in the difference (0 when waived, D-66). */
  fee_cents: number;
  /** The policy's fee before any waiver. */
  policy_fee_cents: number;
  downgrade: 'REFUND' | 'FORFEIT';
  difference_cents: number;
}

export type ExchangeDifference = 'PAY' | 'WAIVE' | 'REFUND' | 'FORFEIT';

export interface ExchangeResult {
  exchange_id: string;
  order_id: string;
  order_number: string;
  difference: ExchangeDifference;
  original_value_cents: number;
  new_value_cents: number;
  amount_due_cents: number;
  amount_refunded_cents: number;
  adjustment_cents: number;
  change_cents: number;
  old_ticket_ids: string[];
  new_ticket_ids: string[];
  refund: { refund_id: string; refund_number: string; stripe: { ok: boolean; message: string | null } } | null;
  print_job_ids: string[];
}

/** What the Orders page hands the Sell page when a cashier starts an exchange (kept in sessionStorage). */
export interface ExchangeContext {
  order_id: string;
  order_number: string;
  customer_name: string | null;
  event_title: string | null;
  tickets: { id: string; label: string }[];
}

export const EXCHANGE_KEY = 'bo.exchange';
export function readExchangeContext(): ExchangeContext | null {
  try {
    const raw = window.sessionStorage.getItem(EXCHANGE_KEY);
    return raw ? (JSON.parse(raw) as ExchangeContext) : null;
  } catch {
    return null;
  }
}
export function writeExchangeContext(context: ExchangeContext | null): void {
  try {
    if (context) window.sessionStorage.setItem(EXCHANGE_KEY, JSON.stringify(context));
    else window.sessionStorage.removeItem(EXCHANGE_KEY);
  } catch {
    // a private window without storage: the exchange simply starts over
  }
}

export interface CardIntent {
  payment_intent_id: string;
  client_secret: string | null;
  tender_type: 'CARD_TERMINAL' | 'CARD_KEYED';
  amount_cents: number;
}

export interface CardStatus {
  payment_intent_id: string;
  intent_status: string;
  ready: boolean;
  reader_action: { status: string } | null;
  failure: string | null;
  card: { brand: string | null; last4: string | null } | null;
}

/** A will-call order for a performance (D-58), as the window lists it. */
export interface WillCallOrder {
  id: string;
  order_number: string;
  status: string;
  customer_name: string | null;
  customer_email: string | null;
  customer_phone: string | null;
  completed_at: string | null;
  fulfillment_printed_at: string | null;
  pickups: number;
  tickets: { id: string; seat_label: string; ticket_type: string | null; printed: boolean }[];
}

export interface OrderSummary {
  id: string;
  order_number: string;
  status: string;
  channel: string;
  delivery_method: string | null;
  customer_name_snapshot: string | null;
  customer_email_snapshot: string | null;
  customer_phone_snapshot: string | null;
  cached_total_due_cents: number;
  completed_at: string | null;
  live_tickets: number;
  first_performance_at: string | null;
  pickups: number;
  /** The group this order is for, and what it booked (D-376). */
  group_name?: string | null;
  group_booking?: string | null;
}

export interface OrderDetail {
  order: {
    id: string;
    order_number: string;
    status: string;
    channel: string;
    customer: { sub: string | null; name: string | null; email: string | null; phone: string | null };
    totals: { total_due_cents: number; paid_cents: number; refunded_cents: number };
    items: { id: string; item_type: string; status: string; event_title_snapshot: string | null; performance_starts_at_snapshot: string | null; section_name_snapshot: string | null; row_label_snapshot: string | null; seat_number_snapshot: string | null; table_label_snapshot?: string | null; ga_area_name_snapshot: string | null; ticket_type_name_snapshot: string | null; face_value_cents: number; discount_cents: number; comp_cents: number; total_cents: number | null; timezone: string | null; ticket: { id: string; status: string; short_code: string; credential_version: number } | null }[];
    payments: { id: string; tender_type: string; status: string; amount_cents: number; card_brand: string | null; card_last4: string | null; cash_tendered_cents: number | null; change_given_cents: number | null; check_number: string | null }[];
    refunds: { id: string; refund_number: string; status: string; cached_total_refund_cents: number; disposition: string }[];
    exchanges: { id: string; status: string; policy_code: string; reason: string | null; original_value_cents: number; new_value_cents: number; adjustment_cents: number; amount_due_cents: number; amount_refunded_cents: number; completed_at: string | null; staff_name: string | null; out: { ticket_id: string; short_code: string; replaced_by_ticket_id: string | null }[]; in: { ticket_id: string; short_code: string; order_item_id: string }[] }[];
  };
  tickets: { id: string; status: string; short_code: string; credential_version: number; holder_name_snapshot: string | null }[];
  pickups: { id: string; ticket_id: string | null; verification_method: string; picked_up_at: string; staff_name: string }[];
  emails: { id: string; template_code: string; status: string; to_email: string; sent_at: string | null }[];
}

/** The attached customer's passes and open renewal offers (D-167). */
export interface CustomerOffers {
  customer_sub: string | null;
  passes: {
    id: string;
    name: string;
    valid_to: string;
    admissions_per_performance: number;
    total_redemptions_allowed: number | null;
    redeemed: number;
    events: { id: string; slug: string; title: string; ticket_type_id: string | null }[];
    /** The seating areas it is good for (D-385); empty is anywhere. */
    areas?: { code: string; name: string }[];
    /** "Good for General Admission only", or empty. */
    area_line?: string;
  }[];
  renewals: {
    instance_id: string;
    product: { id: string; code: string; name: string; description: string | null };
    previous_product: { id: string; code: string; name: string };
    price_cents: number | null;
    expires_at: string | null;
    seats: { section: string | null; row: string | null; seat_number: string | null }[];
    performances: { id: string; event_title: string | null; starts_at: string | null; timezone: string | null }[];
  }[];
  /** Season passes the customer holds (D-373): reserve a game's seats with them. */
  season_passes?: { id: string; number: string; label: string; season_id: string; season_name: string; valid_from: string; valid_to: string; tickets: number }[];
}

/** A comp voucher looked up at the window (D-373). */
export interface VoucherInfo {
  voucher: { id: string; code: string; batch: string; season: string; admits: number; expires_on: string; comp_reason: string };
  games: { event_id: string; title: string; performance_id: string; starts_at: string; eligible: boolean; reason: string | null }[];
}

/** What a refund would pay back, and out of what, before taking it (D-277, D-278). */
export interface RefundEstimate {
  refund_total_cents: number;
  money_back_cents: number;
  receivable_relief_cents: number;
  item_count: number;
  whole_order: boolean;
  returning: { name: string; amount_cents: number }[];
  kept: { name: string; amount_cents: number }[];
  tenders: { payment_id: string; tender_type: string; label: string; amount_cents: number; note: string }[];
  notes: string[];
}

export type RefundPreview = { ok: true; estimate: RefundEstimate } | { ok: false; code: string; message: string };

export const endpoints = {
  /** Entry times, from the public reader the storefront uses (D-232). */
  timedEntry: (performanceId: string) => api.get<TimedEntry>(`/api/public/performances/${performanceId}/timed-entry`),
  session: () => api.get<SessionResponse>('/api/auth/session'),
  logout: () => api.post('/api/auth/logout'),
  pair: (code: string) => api.post<{ device: { id: string; name: string; device_type: string }; token: string }>('/api/devices/pair', { code }),
  pinLogin: (pin: string) => api.post<{ staff: { id: string; display_name: string }; surface: string }>('/api/auth/pin', { pin }),

  context: () => api.get<WindowContext>('/api/box-office/context'),
  openSession: (input: { register_id?: string | null | undefined; payment_terminal_id?: string | null | undefined; ticket_printer_id?: string | null | undefined; receipt_printer_id?: string | null | undefined }) => api.post<{ session: RegisterSession; context: WindowContext }>('/api/box-office/sessions', input),
  closeSession: (id: string, input: { force?: boolean; reason?: string | null }) => api.post<{ session: RegisterSession }>(`/api/box-office/sessions/${id}/close`, input),
  /** Move an open register onto different hardware without closing it (D-266). */
  changeHardware: (input: { payment_terminal_id?: string | null; ticket_printer_id?: string | null; receipt_printer_id?: string | null }) =>
    api.patch<{ session: RegisterSession; context: WindowContext }>('/api/box-office/sessions/current/hardware', input),
  openDrawer: (input: { cash_drawer_id?: string | null; counts?: { denomination_cents: number; quantity: number }[]; opening_float_cents?: number }) => api.post<{ shift: Shift }>('/api/box-office/drawer/open', input),
  drawer: () => api.get<{ shift: Shift | null; expected: DrawerExpected | null; blind: boolean }>('/api/box-office/drawer'),
  movement: (input: { movement_type: string; amount_cents: number; reason: string; manager_pin?: string }) => api.post<{ movement: { id: string } }>('/api/box-office/drawer/movements', input),
  closeDrawer: (input: { counts?: { denomination_cents: number; quantity: number }[]; counted_close_cents?: number }) => api.post<{ shift: Shift; expected: DrawerExpected }>('/api/box-office/drawer/close', input),
  shifts: () => api.get<{ shifts: (Shift & { cashier_name: string })[] }>('/api/box-office/drawer/shifts'),
  shiftReport: (id: string) => api.get<{ shift: Shift; cashier_name: string | null; drawer: { name: string } | null; expected: DrawerExpected | null; payments: { id: string; order_number: string; amount_cents: number; change_given_cents: number | null; captured_at: string }[]; refunds: { id: string; refund_number: string; order_number: string; amount_cents: number; completed_at: string }[]; movements: { id: string; movement_type: string; direction: string; amount_cents: number; reason: string; created_at: string; staff_name: string }[] }>(`/api/box-office/drawer/shifts/${id}`),

  performances: (all = false) => api.get<{ performances: WindowPerformance[] }>(`/api/box-office/performances${all ? '?all=1' : ''}`),
  performance: (id: string) => api.get<PerformanceDetail>(`/api/box-office/performances/${id}`),
  /** `holds` only for a cashier who may sell out of them (D-330). */
  /** `packageCode`: gray out what lies outside that package's seating areas (D-384). */
  availability: (id: string, packageCode?: string) => api.get<{ ready: boolean; units: { unit_id: string; availability: 'AVAILABLE' | 'HELD_BY_YOU' | 'UNAVAILABLE' | 'ACCESSIBLE_ON_REQUEST' }[]; ga_areas: { ga_area_id: string; available_quantity: number }[]; holds?: { id: string; name: string; unit_ids: string[] }[] }>(`/api/box-office/performances/${id}/availability${packageCode ? `?package=${encodeURIComponent(packageCode)}` : ''}`),

  cart: () => api.get<CartView>('/api/box-office/cart'),
  hold: (input: { performance_id: string; ticket_type_id: string; mode: 'seats' | 'ga' | 'best' | 'timed'; unit_ids?: string[]; ga_area_id?: string; timed_entry_slot_id?: string; quantity?: number; allow_split?: boolean }) => api.post<{ held: string[]; cart: CartView }>('/api/box-office/cart/holds', input),
  release: (performance_id: string, unit_ids?: string[]) => api.del<{ cart: CartView }>('/api/box-office/cart/holds', { performance_id, unit_ids }),
  clearCart: () => api.del<{ cleared: boolean }>('/api/box-office/cart'),
  extendCart: () => api.post<{ expires_at: string | null }>('/api/box-office/cart/extend'),
  item: (unitId: string, input: { discount_cents?: number | null; discount_percent?: number | null; comp_reason_id?: string | null; manager_pin?: string }) => api.patch<{ cart: CartView }>(`/api/box-office/cart/items/${unitId}`, input),
  customers: (q: string) => api.get<{ matches: { sub: string | null; name: string | null; email: string | null; phone: string | null; phone_last4: string | null; source: 'account' | 'orders'; verified?: boolean; last_order_number?: string | null }[] }>(`/api/box-office/customers?q=${encodeURIComponent(q)}`),
  /** A customer account made at the window (D-370); unverified until its owner signs in with that address. */
  createCustomer: (input: { name: string; email: string | null; phone: string | null }) =>
    api.post<{ customer: { sub: string; name: string | null; email: string | null; phone: string | null; verified: boolean }; created: boolean }>('/api/box-office/customers', input),
  setCustomer: (input: { sub?: string | null; name?: string | null; email?: string | null; phone?: string | null; organization_name?: string | null; organization_id?: string | null }) => api.put<{ customer: CartView['customer']; flags: CustomerFlag[]; record?: CustomerRecord }>('/api/box-office/cart/customer', input),
  applyPromoCode: (code: string) => api.put<{ cart: CartView }>('/api/box-office/cart/promo-code', { code }),
  removePromoCode: () => api.del<{ cart: CartView }>('/api/box-office/cart/promo-code'),
  /** Extras on the sale (D-377): the whole list, as the storefront sends it. */
  setAddOns: (addOns: { add_on_product_id: string; quantity: number }[]) => api.put<{ cart: CartView }>('/api/box-office/cart/add-ons', { add_ons: addOns }),

  checkGiftCard: (code: string) => api.post<{ masked_identifier: string; available_cents: number }>('/api/box-office/gift-cards/check', { code }),
  /** The attached customer's account credit (D-372). */
  customerCredit: (customerSub: string) =>
    api.get<{ credit: { customer_id: string; balance_cents: number; spendable_cents: number; verified: boolean }; entries: { id: string; kind: string; kind_name: string; amount_cents: number; created_at: string; order_number: string | null }[] }>(`/api/box-office/customers/${encodeURIComponent(customerSub)}/credit`),
  loyaltyRewards: (customerSub: string) => api.get<{ available: boolean; rewards: { reward_id: string; name: string; value_cents: number }[] }>(`/api/box-office/loyalty/rewards?customer_sub=${encodeURIComponent(customerSub)}`),
  cardStart: (amount_cents: number, keyed: boolean) => api.post<{ intent: CardIntent; publishable_key?: string | null }>('/api/box-office/card/start', { amount_cents, keyed }),
  cardStatus: (id: string) => api.get<CardStatus>(`/api/box-office/card/${id}`),
  cardCancel: (id: string) => api.post(`/api/box-office/card/${id}/cancel`),
  simulateTap: () => api.post('/api/box-office/card/simulate-tap'),
  sale: (input: { tenders: Tender[]; delivery: { method: string; print: boolean; email: boolean; receipt: boolean }; contact?: { name?: string | null; email?: string | null; phone?: string | null } | null; balance?: BalanceTerms | null; payment_request?: PaymentRequestInput | null }) => api.post<SaleResult>('/api/box-office/sales', input),

  // Balances (D-71): what an order still owes, taking a payment against it, and its payment link.
  paymentSummary: (orderId: string) => api.get<OrderPaymentSummary>(`/api/box-office/orders/${orderId}/payment`),
  collect: (orderId: string, input: { tenders: Tender[]; receipt_email: boolean; note?: string | null; manager_pin?: string }) => api.post<{ payment: { amount_cents: number; change_cents: number; balance_after_cents: number; paid_in_full: boolean }; summary: OrderPaymentSummary }>(`/api/box-office/orders/${orderId}/payments`, input),
  createPaymentRequest: (orderId: string, input: PaymentRequestInput & { manager_pin?: string }) => api.post<{ payment_request: PaymentRequestSummary; url: string }>(`/api/box-office/orders/${orderId}/payment-requests`, input),
  resendPaymentRequest: (requestId: string, rotate: boolean) => api.post<{ payment_request: PaymentRequestSummary; url: string | null }>(`/api/box-office/payment-requests/${requestId}/resend`, { rotate }),
  copyPaymentRequest: (requestId: string) => api.post<{ url: string }>(`/api/box-office/payment-requests/${requestId}/copy`),
  revokePaymentRequest: (requestId: string, reason: string | null) => api.post<{ payment_request: PaymentRequestSummary }>(`/api/box-office/payment-requests/${requestId}/revoke`, { reason }),

  orders: (q: string) => api.get<{ orders: OrderSummary[] }>(`/api/box-office/orders?q=${encodeURIComponent(q)}`),
  order: (id: string) => api.get<OrderDetail>(`/api/box-office/orders/${id}`),
  pickup: (id: string, input: { ticket_ids?: string[]; verification_method: string; notes?: string | null; print: boolean }) => api.post<{ picked_up: string[]; print_job_ids: string[] }>(`/api/box-office/orders/${id}/pickup`, input),
  reprint: (id: string, ticketId: string, reason: string) => api.post<{ print_job_ids: string[] }>(`/api/box-office/orders/${id}/tickets/${ticketId}/reprint`, { reason }),
  printOrder: (id: string) => api.post<{ print_job_ids: string[] }>(`/api/box-office/orders/${id}/print`),
  willCall: (performanceId: string) => api.get<{ orders: WillCallOrder[] }>(`/api/box-office/performances/${performanceId}/will-call`),
  printWillCall: (performanceId: string, input: { order_ids?: string[]; include_printed?: boolean }) => api.post<{ batch_id: string; orders: number; header_job_ids: string[]; ticket_job_ids: string[] }>(`/api/box-office/performances/${performanceId}/will-call/print`, input),
  receipt: (id: string) => api.post<{ print_job_id: string }>(`/api/box-office/orders/${id}/receipt`),
  emailOrder: (id: string, to_email: string | null) => api.post<{ email_message_id: string }>(`/api/box-office/orders/${id}/email`, { to_email }),
  packages: () => api.get<{ packages: WindowPackage[] }>('/api/box-office/packages'),
  packageTables: (code: string) => api.get<{ tables: { id: string; label: string; section: string | null; seats: number; available: number }[] }>(`/api/box-office/packages/${encodeURIComponent(code)}/tables`),
  addPackage: (input: { package: string; quantity: number; selections?: string[]; seat: { mode: 'best'; section_ids?: string[] } | { mode: 'table'; table_id: string } | { mode: 'seats'; unit_ids: string[]; groups: string[][] } }) => api.post<{ key: string; cart: CartView }>('/api/box-office/cart/packages', input),
  /** The maps a package's seats are picked on (D-330). */
  packageSteps: (code: string, selections: string[]) => api.get<{ steps: PackageMapStep[] }>(`/api/box-office/packages/${encodeURIComponent(code)}/steps${selections.length > 0 ? `?selections=${encodeURIComponent(selections.join(','))}` : ''}`),
  removePackage: (key: string) => api.del<{ removed: boolean; cart: CartView }>(`/api/box-office/cart/packages/${encodeURIComponent(key)}`),
  /** What the attached customer already holds: passes to redeem, seasons to renew (D-167). */
  customerOffers: () => api.get<CustomerOffers>('/api/box-office/cart/customer-offers'),
  // Comp vouchers and season passes (D-373).
  voucher: (code: string) => api.get<VoucherInfo>(`/api/box-office/vouchers/${encodeURIComponent(code)}`),
  addVoucher: (input: { code: string; performance_id: string; placement?: 'BEST_AVAILABLE' | 'GENERAL_ADMISSION' | null }) => api.post<{ voucher: { code: string; batch: string; admits: number }; unit_ids: string[]; event: { title: string }; cart: CartView }>('/api/box-office/cart/voucher', input),
  redeemSeasonPasses: (input: { pass_ids: string[]; performance_id: string }) => api.post<{ unit_ids: string[]; cart: CartView }>('/api/box-office/cart/season-passes', input),
  redeemPass: (input: { pass_id: string; performance_id: string; quantity: number }) => api.post<{ unit_ids: string[]; expires_at: string; cart: CartView }>('/api/box-office/cart/pass', input),
  renew: (instanceId: string) => api.post<{ key: string; product: { id: string; code: string; name: string }; cart: CartView }>('/api/box-office/cart/renewal', { instance_id: instanceId }),
  exchangeQuote: (order_id: string, ticket_ids: string[], waive_fee = false) => api.post<{ quote: ExchangeQuote }>('/api/box-office/exchanges/quote', { order_id, ticket_ids, waive_fee }),
  exchange: (input: { order_id: string; ticket_ids: string[]; difference: ExchangeDifference; tenders: Tender[]; reason?: string | null; print: boolean; email: boolean; waive_fee?: boolean; manager_pin?: string }) => api.post<{ exchange: ExchangeResult }>('/api/box-office/exchanges', input),
  previewRefund: (id: string, input: { scope: 'FULL_ORDER' | 'TICKETS'; order_item_ids?: string[]; nonrefundable_fee_override?: boolean; as_credit?: boolean }) =>
    api.post<RefundPreview>(`/api/box-office/orders/${id}/refunds/preview`, input),
  refund: (id: string, input: { scope: 'FULL_ORDER' | 'TICKETS'; order_item_ids?: string[]; reason_code: string; reason_text?: string | null; manager_pin?: string; as_credit?: boolean }) =>
    api.post<{ refund: { refund_number: string; refund_total_cents: number; credit?: { amount_cents: number; balance_cents: number; email: string | null; verified: boolean } | null } }>(`/api/box-office/orders/${id}/refunds`, input),
  /** Switched behaviors the window offers (D-329). */
  /** What this window offers (D-329, D-369). Loyalty and gift cards are absent unless the API says otherwise. */
  features: () => api.get<{ offer_credit_refunds: boolean; loyalty?: boolean; gift_cards?: boolean; account_credit?: boolean; history_import?: boolean }>('/api/box-office/features'),
  ticketPdfUrl: (orderId: string, ticketId: string) => `/api/box-office/orders/${orderId}/tickets/${ticketId}/pdf`,
};

/** Money as US dollars, grouped: $1,048.00 rather than $1048.00 (D-83). */
const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
export const dollars = (cents: number | null | undefined): string => (cents === null || cents === undefined ? '' : USD.format(cents / 100));

export interface TimedEntry {
  performance_id: string;
  timezone: string;
  slots: { id: string; ticket_type_id: string | null; starts_at: string; ends_at: string | null; available_quantity: number; status: 'AVAILABLE' | 'LIMITED' | 'SOLD_OUT' | 'CLOSED' }[];
}

export function when(value: string | null | undefined, timeZone = 'America/Chicago', style: 'long' | 'short' | 'time' = 'short'): string {
  if (!value) return '';
  try {
    const options: Intl.DateTimeFormatOptions = style === 'long' ? { timeZone, weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' } : style === 'time' ? { timeZone, hour: 'numeric', minute: '2-digit' } : { timeZone, weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
    return new Intl.DateTimeFormat('en-US', options).format(new Date(value));
  } catch {
    return value;
  }
}

// --- D-71: balances, payments over time, payment links ---------------------------------------

export interface BalanceTerms {
  issuance_policy: 'ISSUE_IMMEDIATELY' | 'ISSUE_WHEN_PAID' | 'ISSUE_BUT_DISABLE_ADMISSION_UNTIL_PAID';
  due_at: string | null;
  past_due_policy: 'NO_AUTOMATIC_ACTION' | 'NOTIFY_STAFF' | 'AUTO_CANCEL_AFTER_GRACE_PERIOD';
}

export interface PaymentRequestInput {
  amount_policy: 'FULL_REMAINING_BALANCE_ONLY' | 'AT_LEAST_MINIMUM' | 'CUSTOM_AMOUNT_ALLOWED' | 'FIXED_REQUEST_AMOUNT';
  requested_amount_cents?: number | null;
  minimum_amount_cents?: number | null;
  due_at?: string | null;
  recipient_email?: string | null;
  recipient_name?: string | null;
  send: boolean;
}

export interface PaymentRequestSummary {
  id: string;
  status: string;
  recipient_email_snapshot: string;
  amount_policy: string;
  requested_amount_cents: number;
  due_at: string | null;
  sent_at: string | null;
  last_viewed_at: string | null;
}

export interface OrderPaymentSummary {
  balance: { amount_due_cents: number; paid_cents: number; balance_due_cents: number; in_flight_cents: number };
  payment_status: string;
  payment_due_at: string | null;
  past_due: boolean;
  days_past_due: number;
  ticket_issuance_policy: string;
  payments: { id: string; tender_type: string; status: string; payment_method: string | null; card_last4: string | null; check_number: string | null; captured_amount_cents: number; amount_cents: number; created_at: string; staff_name: string | null }[];
  requests: PaymentRequestSummary[];
}
